/* =====================================================================
   🔧 Theme Sauce — buzzwoo E-Com Kitchen
   bw-iv-header-mega-paged.js
   BW Ideenverlag — Paginiertes Megamenü (Rail + Panel)

   Custom Element <bw-mega-paged>, gehört zu
   snippets/bw-iv-header-mega-paged.liquid.

   Aufgaben (bewusst nur diese):
   1. Panel-Wechsel: Hover/Focus auf einer Rail-Zeile zeigt deren Panel,
      `aria-expanded` an den Rail-Zeilen läuft mit.
   2. Pagination der Rail: Pfeile + Dots blättern die Rail-Seiten.
   3. Tastatur: ↑/↓ (und Home/End) wechseln die Rail-Zeile — der Fokus zieht
      das Panel über den vorhandenen focus-Listener nach.

   Escape ist NICHT hier implementiert: Ignite hängt an jedes <details>
   (außer in Drawern) einen keyup-Handler, der das nächste offene
   `details[open]` um das Event-Ziel schließt und die Summary fokussiert
   (global.js `onKeyUpEscape`). Die Rail liegt in genau so einem <details>,
   Escape funktioniert also bereits — ein zweiter Handler würde doppelt
   schließen.

   Progressive Enhancement: Markup ist ohne JS benutzbar — die erste Rail-Zeile
   und ihr Panel sind server-seitig aktiv, alle Rail-Zeilen sind echte Links auf
   ihre Kategorie, und ohne JS bleiben alle Rail-Seiten sichtbar. Dieses Skript
   blendet die Folgeseiten erst beim Initialisieren aus.

   Kein Klick-Hijacking: die Rail-Zeilen navigieren weiterhin ganz normal.

   Cleanup: alle Listener laufen über EIN AbortController-Signal;
   `disconnectedCallback` ruft `abort()` — der Theme-Editor rendert die
   Header-Section bei jeder Einstellung neu, ohne Cleanup sammelten sich
   pro Rerender neue Listener-Instanzen.
   ===================================================================== */

class BWMegaPaged extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.railPages = Array.from(this.querySelectorAll('.bw-mpm__rail'));
    this.railLinks = Array.from(this.querySelectorAll('.bw-mpm__rail-link'));
    this.panels = Array.from(this.querySelectorAll('.bw-mpm__panel'));
    this.dots = Array.from(this.querySelectorAll('[data-bw-mpm-page]'));
    this.prevButton = this.querySelector('[data-bw-mpm-prev]');
    this.nextButton = this.querySelector('[data-bw-mpm-next]');

    // Ohne Rail-Zeilen gibt es nichts zu steuern — Markup unangetastet lassen.
    if (!this.railLinks.length) return;
    this.initialized = true;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.railLinks.forEach((link) => {
      link.addEventListener('pointerenter', () => this.activatePanel(link), { signal });
      link.addEventListener('focus', () => this.activatePanel(link), { signal });
      link.addEventListener('keydown', (event) => this.onRailKeydown(event, link), { signal });
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.showPage(parseInt(dot.dataset.bwMpmPage, 10));
      }, { signal });
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.showPage(this.page - 1), { signal });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.showPage(this.page + 1), { signal });
    }

    this.showPage(1);
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    // Beim Wieder-Einhängen (Editor-Rerender) sauber neu initialisieren.
    this.initialized = false;
  }

  get pageCount() {
    return this.railPages.length || 1;
  }

  /** Rail-Zeilen der aktuell sichtbaren Seite, in DOM-Reihenfolge. */
  get visibleRailLinks() {
    return this.railLinks.filter((link) => !link.closest('.bw-mpm__rail')?.hidden);
  }

  /**
   * ↑/↓ wandern innerhalb der sichtbaren Rail-Seite, Home/End springen an
   * Anfang/Ende. Der Fokuswechsel löst den focus-Listener aus, der das Panel
   * umschaltet — es gibt also nur EINEN Pfad, der Panels aktiviert.
   * ←/→ bleiben frei (Ignite nutzt sie nicht im Megamenü, aber die Rail-Links
   * sind Links, dort wäre ein horizontales Mapping überraschend).
   */
  onRailKeydown(event, link) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const links = this.visibleRailLinks;
    const index = links.indexOf(link);
    if (index === -1) return;

    let target;
    if (event.key === 'ArrowDown') target = links[Math.min(index + 1, links.length - 1)];
    if (event.key === 'ArrowUp') target = links[Math.max(index - 1, 0)];
    if (event.key === 'Home') target = links[0];
    if (event.key === 'End') target = links[links.length - 1];

    if (!target || target === link) {
      // Am Rand: Standard-Scrollen der Seite trotzdem unterdrücken, damit das
      // Menü unter dem Nutzer nicht wegscrollt.
      event.preventDefault();
      return;
    }

    event.preventDefault();
    target.focus();
  }

  /** Zeigt das zur Rail-Zeile gehörende Panel, blendet die übrigen aus. */
  activatePanel(link) {
    const panelId = link.dataset.bwMpmPanel;
    if (!panelId) return;

    this.railLinks.forEach((other) => {
      const isActive = other === link;
      other.classList.toggle('is-active', isActive);
      other.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
  }

  /**
   * Blättert die Rail auf die gegebene Seite (1-basiert) und aktiviert deren
   * ersten Eintrag, damit Rail und Panel nie auseinanderlaufen.
   * Der Fokus bleibt auf dem Pager-Button — sonst wäre mehrfaches Blättern
   * per Tastatur nicht möglich.
   */
  showPage(pageNumber) {
    const page = Math.min(Math.max(pageNumber, 1), this.pageCount);
    this.page = page;

    this.railPages.forEach((railPage) => {
      railPage.hidden = parseInt(railPage.dataset.page, 10) !== page;
    });

    this.dots.forEach((dot) => {
      const isCurrent = parseInt(dot.dataset.bwMpmPage, 10) === page;
      if (isCurrent) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (this.prevButton) this.prevButton.disabled = page === 1;
    if (this.nextButton) this.nextButton.disabled = page === this.pageCount;

    const firstLinkOnPage = this.railPages
      .find((railPage) => parseInt(railPage.dataset.page, 10) === page)
      ?.querySelector('.bw-mpm__rail-link');

    if (firstLinkOnPage) this.activatePanel(firstLinkOnPage);
  }
}

if (!customElements.get('bw-mega-paged')) {
  customElements.define('bw-mega-paged', BWMegaPaged);
}
